import { CompletedStopSummary } from "@/features/towers/types";
import {
  ETAQuantiles,
  MLTripSummary,
  StopSummary,
} from "@/features/towers/types";
import { buildClusterDisplayNameById } from "@/features/towers/utils/metadataLookup";

import {
  type DeliveryBenchmarkLane,
  getStopBenchmarkDeviationMinutes,
  isArrivedBenchmarkEligibleStop,
} from "../../../utils/deliveryBenchmark";
import { getVisitStatusForStopBenchmark } from "../../../utils/visitStatus";

type DisplayStopSummary = StopSummary & {
  placeName?: string;
  contractualDeviationMinutes?: number;
};

export const addMinutesToTimestamp = (
  timestamp: string,
  minutes: number,
): string =>
  new Date(new Date(timestamp).getTime() + minutes * 60_000).toISOString();

const getEtaMinutes = (
  predictedNextStopEta: ETAQuantiles | undefined,
): number | undefined => {
  if (predictedNextStopEta == null) {
    return undefined;
  }

  const nestedP50 = predictedNextStopEta.percentiles?.p50;
  if (typeof nestedP50 === "number" && Number.isFinite(nestedP50)) {
    return nestedP50;
  }

  const explicitP50 = predictedNextStopEta.p50;
  if (typeof explicitP50 === "number" && Number.isFinite(explicitP50)) {
    return explicitP50;
  }

  const firstNumericValue = Object.values(predictedNextStopEta).find(
    (v): v is number => Number.isFinite(v),
  );
  return firstNumericValue;
};

export const getTripSummaryClusterDisplayName = (
  clusterName: string | undefined,
  clusterId: string | undefined,
): string | undefined => {
  if (clusterName != null && clusterName !== "") {
    return clusterName;
  }

  if (clusterId != null && clusterId !== "") {
    return clusterId;
  }

  return undefined;
};

export const buildCompletedStopSummaries = (
  stopSummaries: DisplayStopSummary[],
  lane: DeliveryBenchmarkLane,
  allTripsForClusterLabels: readonly MLTripSummary[] = [],
): CompletedStopSummary[] => {
  const clusterDisplayNameById = buildClusterDisplayNameById([
    ...allTripsForClusterLabels,
  ]);

  return stopSummaries
    .filter((stopSummary) => isArrivedBenchmarkEligibleStop(stopSummary))
    .map((stopSummary) => ({
      sequencePosition: stopSummary.sequencePosition,
      placeName:
        clusterDisplayNameById.get(stopSummary.clusterId) ??
        stopSummary.placeName ??
        stopSummary.clusterName ??
        stopSummary.clusterId,
      status: getVisitStatusForStopBenchmark(stopSummary, lane) ?? "UNKNOWN",
      deviationMinutes: getStopBenchmarkDeviationMinutes(stopSummary, lane),
    }));
};

export const getCurrentEtaTime = (trip: MLTripSummary): string | undefined => {
  const etaMinutes = getEtaMinutes(trip.predictedNextStopEta);
  if (
    etaMinutes == null ||
    trip.lastUpdated == null ||
    trip.lastUpdated === ""
  ) {
    return undefined;
  }

  const lastUpdatedMs = new Date(trip.lastUpdated).getTime();
  if (!Number.isFinite(lastUpdatedMs)) {
    return undefined;
  }

  return new Date(lastUpdatedMs + etaMinutes * 60_000).toISOString();
};
